import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Copie un texte dans le presse-papier de façon robuste.
 *
 * Essaie d'abord l'API Clipboard moderne (navigator.clipboard.writeText),
 * qui nécessite un contexte sécurisé (HTTPS / localhost) et la permission
 * clipboard-write. En cas d'échec (WebView, Cordova, HTTP, iOS WKWebView
 * sans permission, etc.), utilise l'API legacy document.execCommand('copy')
 * via un textarea temporaire.
 *
 * @returns true si la copie a réussi, false sinon.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  // 1. API Clipboard moderne
  if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Continue vers le fallback
    }
  }

  // 2. Fallback legacy (execCommand) — fonctionne dans la plupart des WebView
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    // Placer hors écran pour éviter le scroll
    textarea.style.position = "fixed";
    textarea.style.top = "-9999px";
    textarea.style.left = "-9999px";
    textarea.style.opacity = "0";
    textarea.setAttribute("readonly", "");
    document.body.appendChild(textarea);

    // Sélectionner le contenu
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);

    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);

    if (ok) return true;
  } catch {
    // execCommand non supporté ou échoué
  }

  // 3. Dernier recours : sélection manuelle (l'utilisateur copie lui-même)
  return false;
}

/**
 * Navigate back safely — uses browser history if there's a real
 * previous page, otherwise navigates to the fallback route.
 *
 * Use this instead of `navigate(-1)` on pages that can be
 * accessed directly (login, register, dashboard) where there
 * might be no previous history entry.
 *
 * The check uses `history.length > 1` AND verifies that the
 * current page wasn't the first one loaded (via performance API).
 */
export function safeBack(
  navigate: (to: any) => void,
  fallback = "/"
) {
  if (typeof window === "undefined") {
    (navigate as (s: string) => void)(fallback);
    return;
  }

  // Check if this is the first page in the tab's history
  const navEntry = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
  const isFirstPage = navEntry?.type === "navigate" && window.history.length <= 1;

  if (window.history.length > 1 && !isFirstPage) {
    (navigate as (n: number) => void)(-1);
  } else {
    (navigate as (s: string) => void)(fallback);
  }
}